/**
 * El WhatsApp oficial en la ficha de Público (pedido de Alan del 17/10/2026: «todo lo toma desde Público»): lo que antes
 * sólo estaba en la ficha de la Base de datos, que salió del menú.
 *
 * Con un contacto en la Base del oficial (`baseContactoId`), se lee su ficha (`GET /base/contactos/:id`):
 * - su consentimiento, con de dónde, cuándo y la carga, y el historial entero (la constancia del art. 5 de la Ley
 *   25.326). Registrarlo o quitarlo es del equipo, con motivo (`ModalConsentimiento`, el mismo de la Base);
 * - su celular, enmascarado: «Ver número completo» lo pide con `?reveal=true` y el backend deja anotado quién lo vio.
 *   No se guarda en ningún lado del navegador;
 * - la supresión (art. 16), del equipo: borra su contacto del oficial de verdad y deja la constancia
 *   (`ModalSuprimir`). No borra la base de ventas ni Snov: lo dice antes.
 * Sin contacto en la Base, se dice y el equipo tiene el camino para cargarlo con su consentimiento.
 */

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Eye, Loader2, MessageCircle, OctagonAlert, Trash2 } from "lucide-react";
import type { CambioConsentimiento, ContactoFicha, EntradaSupresion, ResultadoSupresion } from "../../wa-oficial/types.base";
import { baseApi } from "../../wa-oficial/baseApi";
import { destinoParaMostrar, nombreDeQuienParaCliente } from "../../wa-oficial/logic/formato";
import { FUENTE_BASE, R_CARGAS, llamar, textoFallo } from "../../wa-oficial/components/base/comun";
import { ChipConsentimiento } from "../../wa-oficial/components/base/ChipConsentimiento";
import { ModalConsentimiento } from "../../wa-oficial/components/base/ModalConsentimiento";
import { ModalSuprimir } from "../../wa-oficial/components/base/ModalSuprimir";
import { ErrorTarjeta, Esqueleto } from "../../wa-oficial/components/estadisticas/comun";
import { fecha, fechaHora } from "../../campana-nueva/utils/format";

/** Un día "YYYY-MM-DD" se muestra al mediodía: a las 00:00 UTC cae el día anterior en Argentina. */
const dia = (d: string | null | undefined) => (d ? fecha(/^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T12:00:00` : d) : "—");

const tarjeta = "rounded-2xl border border-slate-100 bg-white p-5";

function Titulo({ derecha }: { derecha?: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
      <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><MessageCircle size={14} className="text-emerald-600" /> WhatsApp oficial · consentimiento</h2>
      {derecha}
    </div>
  );
}

function Dato({ etiqueta, valor }: { etiqueta: string; valor: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3 py-1.5 border-b border-slate-50 last:border-0 text-sm">
      <span className="text-slate-500">{etiqueta}</span>
      <span className="text-slate-900 text-right">{valor}</span>
    </div>
  );
}

/** `baja`: pidió la baja de WhatsApp (lo dice el Público): no le llega nada, ni por el sí de la base de ventas. */
export function ConsentimientoOficial({ baseContactoId, equipo, baja = false }: { baseContactoId: string; equipo: boolean; baja?: boolean }) {
  const [ficha, setFicha] = useState<ContactoFicha | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [noEsta, setNoEsta] = useState(false);
  const [revelando, setRevelando] = useState(false);
  const [errorRevelar, setErrorRevelar] = useState("");
  const [modal, setModal] = useState<"registrar" | "quitar" | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [errorModal, setErrorModal] = useState("");
  const [enNoContactar, setEnNoContactar] = useState(false);
  const [suprimir, setSuprimir] = useState(false);
  const [suprimiendo, setSuprimiendo] = useState(false);
  const [errorSuprimir, setErrorSuprimir] = useState("");
  /** la supresión hecha: si su número quedó en no contactar */
  const [suprimido, setSuprimido] = useState<{ enNoContactar: boolean } | null>(null);

  const cargar = useCallback(async () => {
    if (!baseContactoId) return;
    setCargando(true);
    const l = await llamar<ContactoFicha>(() => baseApi.contacto(baseContactoId));
    setCargando(false);
    if (!l.ok || !l.data) {
      if (!l.ok && l.status === 404) { setNoEsta(true); return; }
      setError(l.ok ? "El servidor no devolvió el contacto." : textoFallo(l));
      return;
    }
    setFicha(l.data);
    setError("");
  }, [baseContactoId]);

  useEffect(() => {
    setFicha(null);
    setNoEsta(false);
    setSuprimido(null);
    void cargar();
  }, [cargar]);

  const revelar = async () => {
    setRevelando(true);
    setErrorRevelar("");
    const l = await llamar<ContactoFicha>(() => baseApi.contacto(baseContactoId, true));
    setRevelando(false);
    if (!l.ok || !l.data) { setErrorRevelar(l.ok ? "El servidor no devolvió el número." : textoFallo(l)); return; }
    setFicha(l.data);
  };

  const cambiar = async (c: CambioConsentimiento) => {
    setGuardando(true);
    setErrorModal("");
    setEnNoContactar(false);
    const l = await llamar<ContactoFicha>(() => baseApi.cambiarConsentimiento(baseContactoId, c));
    setGuardando(false);
    if (!l.ok || !l.data) {
      setErrorModal(l.ok ? "El servidor no devolvió el contacto." : textoFallo(l));
      setEnNoContactar(!l.ok && l.code === "EN_NO_CONTACTAR");
      return;
    }
    // la respuesta trae el número enmascarado: si estaba a la vista, se conserva lo que ya se vio
    setFicha((f) => (f && !f.enmascarado ? { ...l.data, destino: f.destino, enmascarado: false } : l.data));
    setModal(null);
  };

  const borrar = async (s: EntradaSupresion) => {
    setSuprimiendo(true);
    setErrorSuprimir("");
    const l = await llamar<ResultadoSupresion>(() => baseApi.suprimirContacto(baseContactoId, s));
    setSuprimiendo(false);
    if (!l.ok || !l.data?.suprimido) { setErrorSuprimir(l.ok ? "El servidor no confirmó el borrado." : textoFallo(l)); return; }
    setSuprimir(false);
    setSuprimido({ enNoContactar: l.data.dejadoEnNoContactar });
    setFicha(null);
  };

  // Público junta otras fuentes: la persona sigue ahí si está en la base de ventas o en Snov, y el sí de ventas vale
  // para el oficial. Por eso se dice qué pasa con su número, no que desaparece.
  if (suprimido) {
    return (
      <section className={tarjeta} data-testid="ficha-oficial">
        <Titulo />
        <p role="status" className="text-sm text-slate-700">
          Se borró su contacto del WhatsApp oficial y quedó la constancia de la supresión.{" "}
          {suprimido.enNoContactar
            ? "Su número quedó en no contactar: no le llega nada más por WhatsApp."
            : "Su número no quedó en no contactar: si dijo que sí en la base de ventas, por el oficial le puede seguir llegando."}
        </p>
      </section>
    );
  }

  if (!baseContactoId || noEsta) {
    return (
      <section className={tarjeta} data-testid="ficha-oficial">
        <Titulo />
        <p className="text-sm text-slate-600">
          {noEsta ? "Su contacto del WhatsApp oficial ya no está (puede que se haya borrado)." : "No está en los contactos con consentimiento del WhatsApp oficial."}
          {baja ? " Pidió la baja de WhatsApp: no le llega nada." : " Por el oficial le puede llegar igual si dijo que sí en la base de ventas."}
        </p>
        {equipo && (
          <Link to={`${R_CARGAS}/nueva`} className="mt-2 inline-block text-sm font-semibold text-violet-700 underline">
            Cargar contactos con consentimiento
          </Link>
        )}
      </section>
    );
  }

  if (!ficha) {
    if (cargando || !error) return <Esqueleto alto="h-40" etiqueta="Cargando su contacto del WhatsApp oficial" />;
    return <ErrorTarjeta texto={`No pudimos traer su contacto del WhatsApp oficial. ${error}`} onReintentar={() => void cargar()} />;
  }

  const c = ficha.consentimiento;
  const bajasReales = ficha.noContactar.filter((b) => b.activo && b.simulado !== true);

  return (
    <section className={tarjeta} data-testid="ficha-oficial">
      <Titulo derecha={<ChipConsentimiento valor={c.optIn} grande />} />
      <Dato
        etiqueta="Celular"
        valor={
          <span className="inline-flex flex-wrap items-center justify-end gap-x-2">
            <span className="font-mono" data-testid="celular-oficial">{destinoParaMostrar(ficha.destino, !ficha.enmascarado)}</span>
            {ficha.enmascarado && (
              <button type="button" onClick={() => void revelar()} disabled={revelando}
                className="inline-flex items-center gap-1 text-xs font-semibold text-violet-700 hover:underline disabled:opacity-50">
                {revelando ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />} Ver número completo
              </button>
            )}
          </span>
        }
      />
      {ficha.enmascarado && <p className="text-[11px] text-slate-400 text-right -mt-1">Queda registrado que lo viste</p>}
      {errorRevelar && <p className="text-xs text-red-600" role="alert">{errorRevelar}</p>}
      <Dato
        etiqueta="Puede recibir"
        valor={ficha.puedeRecibir.ok
          ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 size={13} /> Sí</span>
          : <span className="inline-flex items-center gap-1 text-red-700"><OctagonAlert size={13} /> {ficha.puedeRecibir.texto || "No"}</span>}
      />
      <Dato etiqueta="De dónde" valor={c.fuente ? FUENTE_BASE[c.fuente] ?? c.fuente : "—"} />
      {c.detalle && <Dato etiqueta="Detalle" valor={c.detalle} />}
      <Dato etiqueta="Fecha" valor={c.fecha ? dia(c.fecha) : "—"} />
      {c.carga && (
        <Dato etiqueta="Carga" valor={<Link to={`${R_CARGAS}/${encodeURIComponent(c.carga.id)}`} className="text-violet-700 hover:underline">{c.carga.nombre}</Link>} />
      )}

      {equipo && (
        <div className="mt-3 flex gap-2 flex-wrap">
          {c.optIn !== true && (
            <button type="button" onClick={() => { setErrorModal(""); setEnNoContactar(false); setModal("registrar"); }}
              className="h-9 px-3 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700">
              Registrar consentimiento
            </button>
          )}
          {c.optIn !== false && (
            <button type="button" onClick={() => { setErrorModal(""); setEnNoContactar(false); setModal("quitar"); }}
              className="h-9 px-3 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50">
              Quitar consentimiento
            </button>
          )}
        </div>
      )}

      <div className="mt-4">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Historial del consentimiento</h3>
        {ficha.historialConsentimiento.length ? (
          <ul className="space-y-2 text-sm" data-testid="historial-consentimiento">
            {[...ficha.historialConsentimiento].reverse().map((h, i) => (
              <li key={`${h.at}-${i}`} className="border-l-2 border-slate-100 pl-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <ChipConsentimiento valor={h.optIn} />
                  {/* `por` es el mail del token: a una persona de un cliente, alguien del equipo es «Equipo Deenex». */}
                  <span className="text-xs text-slate-500">{fechaHora(h.at)} · {(equipo ? h.por : nombreDeQuienParaCliente(h.por)) || "—"}</span>
                </div>
                <p className="text-xs text-slate-600 mt-0.5">
                  {FUENTE_BASE[h.fuente] ?? h.fuente}{h.detalle ? `: ${h.detalle}` : ""}{h.fecha ? ` · del ${dia(h.fecha)}` : ""}{h.carga ? ` · carga «${h.carga.nombre}»` : ""}
                </p>
                {h.motivo && <p className="text-xs text-slate-500 italic">Motivo: {h.motivo}</p>}
              </li>
            ))}
          </ul>
        ) : <p className="text-sm text-slate-500">Sin cambios registrados.</p>}
      </div>

      {/* La supresión (Ley 25.326) la ejecuta Deenex: un cliente se la pide a su contacto. */}
      {equipo ? (
        <div className="mt-5 border-t border-red-100 pt-4">
          <button type="button" onClick={() => { setErrorSuprimir(""); setSuprimir(true); }}
            className="h-10 px-4 rounded-xl border border-red-200 bg-white text-sm font-semibold text-red-700 hover:bg-red-50 inline-flex items-center gap-2">
            <Trash2 size={15} /> Borrar su contacto del oficial (derecho de supresión)
          </button>
          <p className="text-xs text-slate-500 mt-1.5">Borra su contacto del WhatsApp oficial y deja la constancia. No borra la base de ventas ni Snov.</p>
        </div>
      ) : (
        <p className="text-xs text-slate-500 mt-4">Si esta persona pide que borren sus datos (derecho de supresión), pedíselo a tu contacto de Deenex.</p>
      )}

      {modal && (
        <ModalConsentimiento modo={modal} busy={guardando} error={errorModal} enNoContactar={enNoContactar}
          onConfirmar={(x) => void cambiar(x)} onCancelar={() => setModal(null)} />
      )}
      {suprimir && (
        <ModalSuprimir yaEnNoContactar={bajasReales.length > 0} busy={suprimiendo} error={errorSuprimir}
          onConfirmar={(x) => void borrar(x)} onCancelar={() => setSuprimir(false)} />
      )}
    </section>
  );
}
